import React, { useState, useEffect } from "react";

/**
 * AccessHistory — the second half of "who can see this profile?"
 *
 * AccessPanel answers the question in the present tense: who holds access
 * right now, and why. That is the question a coordinator asks before adding
 * someone. This one answers it in the past tense — who has actually been in
 * here, and who changed who can get in — which is the question asked after
 * something has gone wrong, and the one a family asks about their own child.
 *
 * WHY IT LIVES INSIDE THE SAME DIALOG
 *
 * Filed separately it would be an audit feature: a page nobody opens until
 * there is an incident, by which point they have to learn where it is. As a
 * tab on the panel they already use, it is one click from "here is who can
 * see Maris" to "here is who did". The second question follows the first
 * naturally enough that the UI should not make them navigate between them.
 *
 * EVERY ROW IS A SENTENCE, NOT A RECORD
 *
 * The server sends `action` — `grant.revoked`, `client.accessed`. Rendering
 * those verbatim would put our vocabulary in front of someone who never
 * agreed to learn it. `describe()` turns each into plain language with the
 * names already resolved, and an action we have no phrasing for still renders
 * something readable rather than disappearing — an audit trail that silently
 * drops what it does not recognise is worse than one showing an odd row.
 *
 * THE NAME SHOWN IS THE NAME AT THE TIME
 *
 * `actor_label` is frozen when the event is written, so a year-old row reads
 * under the name in force when it happened. When the person has since been
 * renamed the server also sends `actor_current_name`, and we show both —
 * "Dawn L. (now Dawn Okafor)". Showing only the old name strands the reader;
 * showing only the new one is the thing an audit trail must never do.
 */

// Mirrors audit.CATEGORIES. The server sends the authoritative list on every
// response, so this is only the ORDER and the wording — the set itself is not
// duplicated here.
const TAB_LABELS = {
  all:            "Everything",
  viewed:         "Views",
  access_changed: "Access changes",
  identity:       "Name changes",
  other:          "Other",
};
const TAB_ORDER = ["all", "access_changed", "viewed", "identity", "other"];

function dayKey(iso) {
  return iso ? iso.slice(0, 10) : "";
}

function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const yest  = new Date(Date.now() - 86400000);
  const same  = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "Today";
  if (same(d, yest))  return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function timeLabel(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function actorName(e) {
  if (e.actor_current_name && e.actor_current_name !== e.actor_label) {
    return `${e.actor_label} (now ${e.actor_current_name})`;
  }
  return e.actor_label;
}

/**
 * One event as a sentence.
 *
 * Exported for tests: the phrasings ARE the feature, and asserting them
 * through fifteen rendered rows would test the list rather than the language.
 */
export function describe(e, clientName) {
  const who  = actorName(e);
  const whom = e.target_label || "someone";

  switch (e.action) {
    case "client.accessed":
      return `${who} opened the profile`;
    case "share_link.accessed":
      return `${who} opened the profile through a share link`;
    case "share_link.created":
      return `${who} created a share link`;
    case "share_link.revoked":
      return `${who} revoked a share link`;
    case "grant.created":
      return `${who} gave ${whom} access`;
    case "grant.revoked":
      return `${who} removed ${whom}'s access`;
    case "grant.role_changed":
      return `${who} changed ${whom}'s role`;
    case "invitation.created":
      return `${who} invited ${e.detail?.email || whom}`;
    case "invitation.accepted":
      return `${who} accepted an invitation`;
    case "invitation.revoked":
      return `${who} withdrew an invitation`;
    case "client.identity_changed": {
      const was = e.detail?.previous_name;
      return was
        ? `${who} changed the name from ${was} to ${clientName}`
        : `${who} changed the profile's name`;
    }
    default:
      // Deliberately not "unknown event". Someone reading an audit trail
      // wants to know something happened even when we have no phrasing for
      // it, and the raw action is more use to them than a shrug.
      return `${who} — ${e.action}`;
  }
}

export default function AccessHistory({ client }) {
  const [events, setEvents]   = useState(null);
  const [tab, setTab]         = useState("all");
  const [error, setError]     = useState(null);

  useEffect(() => {
    let live = true;
    setEvents(null);
    setError(null);
    const qs = tab === "all" ? "" : `?category=${tab}`;
    fetch(`/api/clients/${client.id}/audit${qs}`)
      .then(r => {
        if (!r.ok) throw new Error("Couldn't load the history.");
        return r.json();
      })
      .then(d => { if (live) setEvents(d.events || []); })
      .catch(err => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [client.id, tab]);

  // null means still loading; [] means nothing happened. A spinner and an
  // empty list say opposite things and must not look alike — the same
  // distinction the client lists had to learn.
  const loading = events === null;
  const empty   = events !== null && events.length === 0;

  const days = [];
  for (const e of events || []) {
    const k = dayKey(e.at);
    const last = days[days.length - 1];
    if (last && last.key === k) last.rows.push(e);
    else days.push({ key: k, at: e.at, rows: [e] });
  }

  return (
    <div>
      <div style={s.tabs} role="tablist">
        {TAB_ORDER.map(k => (
          <button key={k} type="button" role="tab"
                  aria-selected={tab === k}
                  style={{ ...s.tab, ...(tab === k ? s.tabOn : {}) }}
                  onClick={() => setTab(k)}>
            {TAB_LABELS[k]}
          </button>
        ))}
      </div>

      {error && <div style={s.error}>{error}</div>}
      {loading && !error && <p style={s.muted}>Loading…</p>}

      {empty && (
        <div style={s.empty}>
          {tab === "all"
            ? "Nothing recorded for this profile yet."
            : `No ${TAB_LABELS[tab].toLowerCase()} recorded yet.`}
        </div>
      )}

      {days.map(d => (
        <div key={d.key} style={s.day}>
          <div style={s.dayTitle}>{dayLabel(d.at)}</div>
          {d.rows.map(e => (
            <div key={e.id} style={s.row}>
              <span style={s.time}>{timeLabel(e.at)}</span>
              <span style={s.text}>{describe(e, client.display_name)}</span>
            </div>
          ))}
        </div>
      ))}

      {/* Said once, at the bottom, rather than as a caveat on every row. A
          view is recorded once a day per person: five page loads in an
          afternoon are one line, and someone counting rows to measure
          attention would otherwise be counting the wrong thing. */}
      {!loading && !empty && (
        <div style={s.footnote}>
          Views are recorded once a day for each person.
        </div>
      )}
    </div>
  );
}

const s = {
  tabs:      { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "var(--space-4)" },
  tab:       { fontSize: "var(--text-xs)", padding: "5px 11px", borderRadius: 999, border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text-secondary)", cursor: "pointer" },
  tabOn:     { background: "var(--color-accent-subtle)", border: "1px solid var(--color-accent)", color: "var(--color-accent)", fontWeight: "var(--weight-medium)" },
  day:       { marginBottom: "var(--space-5)" },
  dayTitle:  { fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-tertiary)", marginBottom: 8 },
  row:       { display: "flex", gap: 12, padding: "7px 0", borderBottom: "1px solid var(--color-border)", fontSize: "var(--text-sm)" },
  time:      { color: "var(--color-text-tertiary)", fontSize: "var(--text-xs)", minWidth: 62, paddingTop: 2, flexShrink: 0 },
  text:      { color: "var(--color-text-primary)", lineHeight: "var(--leading-normal)" },
  muted:     { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)" },
  empty:     { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", padding: "var(--space-6) 0", textAlign: "center" },
  error:     { fontSize: "var(--text-sm)", color: "var(--color-critical)", marginBottom: "var(--space-3)" },
  footnote:  { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: "var(--space-4)" },
};
